import { ClaimRecord, ClaimRecordStatus } from '@typesApp/ClaimRecord'
import { FooterConfiguration, PageFlowConfiguration, QuoteStatusViewMap, ViewName } from '@typesApp/PageFlow'
import { QuoteStatus } from '@typesApp/QuoteData'

const QUALIFIERS = {
  HAS_REFERRER_NO_PRESCRIPTION: ':HAS_REFERRER_NO_PRESCRIPTION',
  HAS_REFERRER_WITH_PRESCRIPTION: ':HAS_REFERRER_WITH_PRESCRIPTION',
  NO_PRODUCT_INCLUDED_IN_PRESCRIPTION: ':NO_PRODUCT_INCLUDED_IN_PRESCRIPTION',
} as const

type QualifierFunction = (claimRecord: ClaimRecord) => string | undefined

type PageFlowUtilReturn = {
  hasPrescription: (claimRecord: ClaimRecord) => boolean
  hasReferrer: (claimRecord: ClaimRecord) => boolean
  getResumeToView: (config: PageFlowConfiguration | undefined, claimRecord: ClaimRecord) => ViewName | null
  getFooterConfiguration: (config: PageFlowConfiguration | undefined, viewName: ViewName) => FooterConfiguration | null
}

const pageFlowUtil = (): PageFlowUtilReturn => {
  const hasPrescription = (claimRecord: ClaimRecord): boolean => {
    return Array.isArray(claimRecord?.prescriptions) && claimRecord.prescriptions.length > 0
  }

  const hasReferrer = (claimRecord: ClaimRecord): boolean => {
    const referrer = claimRecord?.claimData?.referrer
    return Boolean(referrer?.type) && Boolean(referrer?.firstName) && Boolean(referrer?.lastName)
  }

  const noProductIncludedInPrescription = (claimRecord: ClaimRecord): boolean => {
    const prescriptions = claimRecord?.prescriptions ?? []
    const products = claimRecord?.eligibleProducts ?? []
    return (
      prescriptions.length > 0 &&
      products.length > 0 &&
      products.filter(product => product.includedInPrescription).length === 0
    )
  }

  // qualifiers are applicable to specific claim record statuses
  const QUALIFIER_MAP = {
    PRESCRIPTIONS_REQUIRED: {
      getQualifier: claimRecord => {
        if (hasReferrer(claimRecord)) {
          return hasPrescription(claimRecord)
            ? QUALIFIERS.HAS_REFERRER_WITH_PRESCRIPTION
            : QUALIFIERS.HAS_REFERRER_NO_PRESCRIPTION
        }
      },
    },

    PRESCRIPTIONS_FOR_REVIEW: {
      getQualifier: claimRecord => {
        if (noProductIncludedInPrescription(claimRecord)) {
          return QUALIFIERS.NO_PRODUCT_INCLUDED_IN_PRESCRIPTION
        }
      },
    },

    READY_TO_CLAIM: {
      getQualifier: claimRecord => {
        if (noProductIncludedInPrescription(claimRecord)) {
          return QUALIFIERS.NO_PRODUCT_INCLUDED_IN_PRESCRIPTION
        }
      },
    },
  } as Record<
    | ClaimRecordStatus.PRESCRIPTIONS_REQUIRED
    | ClaimRecordStatus.READY_TO_CLAIM
    | ClaimRecordStatus.PRESCRIPTIONS_FOR_REVIEW,
    { getQualifier: QualifierFunction }
  >

  const getStatusWithQualifier = (claimRecord: ClaimRecord): string => {
    const qualifierMap = QUALIFIER_MAP[claimRecord.status]
    if (typeof qualifierMap?.getQualifier !== 'function') return claimRecord.status

    const qualifier = qualifierMap.getQualifier(claimRecord) ?? ''
    return `${claimRecord?.status}${qualifier}`
  }

  const getResumeToView = (config: PageFlowConfiguration | undefined, claimRecord: ClaimRecord): ViewName | null => {
    const mapping = config?.resumeMapping || ({} as Record<ClaimRecordStatus, ViewName>)
    if (!mapping) {
      return null
    }

    // Note: explicit mapping of claim record status to view takes highest precedence
    let view = mapping[getStatusWithQualifier(claimRecord)]

    if (!view) {
      // wildcard * mapping
      view = mapping['*']
    }

    if (!view) {
      return null
    }

    if (typeof view === 'string') {
      return view as ViewName
    }

    // secondary mapping by quote status
    return getResumeToViewByQuoteStatus({ viewMap: view, quoteStatus: claimRecord?.claimData?.quote?.status })
  }

  const getResumeToViewByQuoteStatus = (options: {
    viewMap: QuoteStatusViewMap | undefined
    quoteStatus: QuoteStatus | undefined
  }): ViewName | null => {
    const { viewMap, quoteStatus } = options
    if (!viewMap || !quoteStatus) {
      return null
    }

    let view: ViewName | null = null
    if (typeof viewMap.quoteStatusMap === 'object') {
      view = viewMap.quoteStatusMap[quoteStatus] || viewMap.quoteStatusMap['*']
    }

    return view
  }

  const getFooterConfiguration = (
    config: PageFlowConfiguration | undefined,
    viewName: ViewName
  ): FooterConfiguration | null => {
    if (!config?.footerConfiguration) {
      return null
    }

    const ctaOptions = config?.footerConfiguration[viewName] || config?.footerConfiguration['*']

    // set initial max width on link
    const maxWidthStyle = { sm: '200px ' }
    const result: FooterConfiguration = {
      ...ctaOptions,
      cancelCta: {
        ...ctaOptions?.cancelCta,
        sx: {
          maxWidth: ctaOptions?.cancelCta?.renderAs === 'link' ? maxWidthStyle : undefined,
          ...ctaOptions?.cancelCta?.sx,
        },
      },
      submitCta: {
        ...ctaOptions?.submitCta,
        sx: {
          maxWidth: ctaOptions?.submitCta?.renderAs === 'link' ? maxWidthStyle : undefined,
          ...ctaOptions?.submitCta?.sx,
        },
      },
    }

    return result
  }

  return {
    hasPrescription,
    hasReferrer,
    getResumeToView,
    getFooterConfiguration,
  }
}

export const PageFlowUtil = pageFlowUtil()
